import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Tabs, Typography, Tag, Alert, Button, message, Spin } from 'antd';
import {
  GithubOutlined,
  CloudOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  CopyOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { formatDistance } from 'date-fns';
import { it } from 'date-fns/locale';
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from '../../api_config.json';
import './BuildDetailModal.css';

const { Text, Title, Paragraph } = Typography;
const { TabPane } = Tabs;

const TAIL_INTERVAL_MS = 5000; // Poll every 5 seconds for in-progress builds

const BuildDetailModal = ({ build, visible, onClose }) => {
  const [logs, setLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [isTailing, setIsTailing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const logsContainerRef = useRef(null);
  const tailIntervalRef = useRef(null);
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();

  const isInProgress = build?.buildStatus === 'in_progress';

  // Fetch logs (can be called multiple times for tailing)
  const fetchLogs = useCallback(async (isInitialFetch = false) => {
    if (isInitialFetch) {
      setLogsLoading(true);
    }
    try {
      const token = await getTokenSilently();
      const response = await fetch(
        `${config.ciUnified[isProd ? "prod" : "dev"]}/builds/${build.id}/logs`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      const result = await response.json();
      setLogs(result.data?.logs || 'Nessun log disponibile');
    } catch (error) {
      console.error(error);
      if (isInitialFetch) {
        setLogs('Errore nel caricamento dei log');
      }
    } finally {
      if (isInitialFetch) {
        setLogsLoading(false);
      }
    }
  }, [getTokenSilently, isProd, build?.id]);

  // Stop tailing
  const stopTailing = useCallback(() => {
    if (tailIntervalRef.current) {
      clearInterval(tailIntervalRef.current);
      tailIntervalRef.current = null;
    }
    setIsTailing(false);
  }, []);

  // Start tailing for in-progress builds
  const startTailing = useCallback(() => {
    if (tailIntervalRef.current) return; // Already tailing

    setIsTailing(true);
    tailIntervalRef.current = setInterval(() => {
      fetchLogs(false);
    }, TAIL_INTERVAL_MS);
  }, [fetchLogs]);

  // Reset state when build changes or modal closes
  useEffect(() => {
    if (!visible) {
      setLogs(null);
      setActiveTab('overview');
      stopTailing();
    }
  }, [visible, build?.id, stopTailing]);

  // Stop tailing when build is no longer in progress
  useEffect(() => {
    if (!isInProgress && isTailing) {
      stopTailing();
    }
  }, [isInProgress, isTailing, stopTailing]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopTailing();
  }, [stopTailing]);

  // Scroll to bottom when logs are loaded
  useEffect(() => {
    if (logs && logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [logs]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'logs') {
      if (!logs) {
        fetchLogs(true);
      }
      // Start tailing if build is in progress
      if (isInProgress) {
        startTailing();
      }
    } else {
      // Stop tailing when leaving logs tab
      stopTailing();
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success('Copiato negli appunti');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'succeeded':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'in_progress':
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      case 'stopped':
        return <ExclamationCircleOutlined style={{ color: '#722ed1' }} />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded': return 'success';
      case 'failed': return 'error';
      case 'in_progress': return 'processing';
      case 'stopped': return 'warning';
      default: return 'default';
    }
  };

  if (!build) return null;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {build.provider === 'github_actions' ? (
            <GithubOutlined style={{ fontSize: '20px' }} />
          ) : (
            <CloudOutlined style={{ fontSize: '20px' }} />
          )}
          <span>{build.projectName}</span>
          <Tag color={getStatusColor(build.buildStatus)}>
            {build.buildStatus?.toUpperCase()}
          </Tag>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      centered
      styles={{
        body: {
          height: activeTab === 'logs' ? 'calc(90vh - 150px)' : 'auto',
          overflow: 'auto'
        }
      }}
      footer={[
        <Button key="link" type="primary" icon={<LinkOutlined />} onClick={() => window.open(build.buildUrl, '_blank')}>
          Apri in {build.provider === 'github_actions' ? 'GitHub' : 'AWS Console'}
        </Button>,
      ]}
    >
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="Dettagli" key="overview">
            <div className="build-detail-grid">
              <div className="build-detail-section">
                <Title level={5}>Info Build</Title>
                <div className="build-detail-row">
                  <Text type="secondary">Stato:</Text>
                  <span>
                    {getStatusIcon(build.buildStatus)}{' '}
                    <Text strong>{build.buildStatus}</Text>
                  </span>
                </div>
                <div className="build-detail-row">
                  <Text type="secondary">Provider:</Text>
                  <Text>{build.provider === 'github_actions' ? 'GitHub Actions' : 'AWS CodeBuild'}</Text>
                </div>
                <div className="build-detail-row">
                  <Text type="secondary">Durata:</Text>
                  <Text>{build.executionTime ? `${build.executionTime} min` : 'N/D'}</Text>
                </div>
                {build.endTime && (
                  <div className="build-detail-row">
                    <Text type="secondary">Completato:</Text>
                    <Text>{formatDistance(new Date(build.endTime), new Date(), { addSuffix: true, locale: it })}</Text>
                  </div>
                )}
              </div>

              <div className="build-detail-section">
                <Title level={5}>Info Git</Title>
                <div className="build-detail-row">
                  <Text type="secondary">Branch:</Text>
                  <Tag color="blue">{build.branch || 'N/D'}</Tag>
                </div>
                {build.hash && (
                  <div className="build-detail-row">
                    <Text type="secondary">Commit:</Text>
                    <span>
                      <Text code>{build.hash?.substring(0, 7)}</Text>
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(build.hash)}
                      />
                    </span>
                  </div>
                )}
                {build.commitMessage && (
                  <div className="build-detail-row">
                    <Text type="secondary">Messaggio:</Text>
                    <Text>{build.commitMessage}</Text>
                  </div>
                )}
                {build.commitAuthor && (
                  <div className="build-detail-row">
                    <Text type="secondary">Autore:</Text>
                    <Text>{build.commitAuthor}</Text>
                  </div>
                )}
              </div>
            </div>

            {/* Error Info for failed builds */}
            {build.buildStatus === 'failed' && (
              <div style={{ marginTop: '24px' }}>
                <Title level={5}>Dettagli Errore</Title>
                {build.failedStep && (
                  <Alert
                    type="error"
                    message={`Fallito in: ${build.failedStep}`}
                    style={{ marginBottom: '12px' }}
                  />
                )}
                {build.errorSummary && (
                  <Alert
                    type="error"
                    message="Riepilogo Errore"
                    description={
                      <Paragraph
                        code
                        copyable
                        style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
                      >
                        {build.errorSummary}
                      </Paragraph>
                    }
                  />
                )}
              </div>
            )}
          </TabPane>

          <TabPane
            tab={
              <span>
                Log {isTailing && <SyncOutlined spin style={{ marginLeft: 4, fontSize: 12 }} />}
              </span>
            }
            key="logs"
          >
            {logsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin tip="Caricamento log..." />
              </div>
            ) : logs ? (
              <>
                {isTailing && (
                  <div className="build-logs-tailing-indicator">
                    <SyncOutlined spin /> Log in tempo reale (aggiornamento ogni {TAIL_INTERVAL_MS / 1000}s)
                  </div>
                )}
                <div className="build-logs-container" ref={logsContainerRef}>
                  <pre className="build-logs">
                    {logs}
                  </pre>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Text type="secondary">Nessun log disponibile</Text>
              </div>
            )}
          </TabPane>
        </Tabs>
    </Modal>
  );
};

export default BuildDetailModal;
